<template>
  <div class="flex flex-col">
    <div class="relative flex flex-1 print:hidden">
      <div class="relative flex items-center justify-center w-full xl:w-2/3 2xl:w-3/4">
        <div class="relative w-full h-full">
          <div class="relative w-full h-full p-12 xl:p-16">
            <h1 class="flex items-center text-5xl font-bold text-primary-500">
              <span v-html="getFormattedFloorName()" />
              <pulse-loader :loading="isLoading" color="#496331" height="32" width="32" class="inline-flex items-center justify-center" />
            </h1>
            <div class="relative w-full h-full">
              <transition :name="transitionName">
                <div :key="floor.name" class="relative w-full h-full 2xl:-ml-16">
                  <img :src="`plans/floors/${floor.name}.png`" class="absolute inset-0 object-contain w-full h-full transform scale-125 translate-x-8 translate-y-10 2xl:translate-x-0 2xl:scale-150">
                  <inline-svg ref="svg" :src="`plans/floors/${floor.plan}.svg`" class="absolute inset-0 w-full h-full transform scale-125 translate-x-8 translate-y-10 2xl:translate-x-0 2xl:scale-150" @loaded="SvgLoaded" @click="getItemInfo" />
                </div>
              </transition>
            </div>
            <ul v-if="Array.isArray(rooms) && rooms.length > 0" class="absolute bottom-0 flex justify-center w-full my-8 space-x-4 align-middle">
              <li class="flex justify-between text-green-500">
                <div>
                  Available
                </div>
                <div class="flex items-center justify-center w-6 h-6 ml-2 text-xs leading-none text-white bg-green-500 rounded-full">
                  {{ rooms.filter(({ status }) => status === 0).length }}
                </div>
              </li>
              <li class="flex justify-between text-red-500">
                <div>
                  Sold
                </div>
                <div class="flex items-center justify-center w-6 h-6 ml-2 text-xs leading-none text-white bg-red-500 rounded-full">
                  {{ rooms.filter(({ status }) => [1, 2, 6].includes(status)).length }}
                </div>
              </li>
              <li class="flex justify-between text-gray-500">
                <div>
                  On Hold
                </div>
                <div class="flex items-center justify-center w-6 h-6 ml-2 text-xs leading-none text-white bg-gray-500 rounded-full">
                  {{ rooms.filter(({ status }) => status === 3).length }}
                </div>
              </li>
            </ul>
            <div class="absolute bottom-0 left-0 mb-4 ml-12 xl:ml-16">
              <img src="@/assets/compass.svg" class="w-auto h-16" alt="">
            </div>
          </div>
        </div>
      </div>
      <div class="hidden xl:block" />
    </div>
    <div v-if="room" class="absolute inset-x-0 top-0 flex justify-end -ml-16 text-white bg-cover pointer-events-none xl:justify-center print:hidden">
      <div class="w-1/2 xl:w-1/3 2xl:px-20">
        <div v-bg-image="'images/josefin-WS5yjFjycNY-unsplash-lightgreen.jpg'" class="relative flex items-stretch w-full p-8 space-x-12 bg-cover pointer-events-auto xl:p-12">
          <div class="w-full">
            <div class="relative flex flex-col items-end">
              <h1 class="text-3xl font-bold uppercase">
                TYPE {{ unit.name }}
              </h1>
              <h2 class="text-xl font-bold uppercase">
                {{ unit.type.name }}
              </h2>
              <h3 class="text-lg">
                {{ unit.area }} sq.m.
              </h3>
              <span v-if="room.sellingPrice" class="text-2xl font-compressed">
                {{ room.sellingPrice.toLocaleString('th-TH') }}
              </span>
              <div class="flex space-x-1">
                <button v-if="isElectron" class="flex items-center px-2 py-1 space-x-2 text-sm font-bold text-gray-900 bg-gray-200 rounded-sm shadow-sm" :disabled="isPrinting" @click="print">
                  <span>Print</span>
                  <svg v-if="isPrinting" class="w-5 h-5 text-black animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <printer-icon v-else />
                </button>
                <span v-if="room.status === 0" class="px-2 py-1 text-sm font-bold text-white bg-green-500 rounded-sm shadow-sm">
                  Available
                </span>
                <span v-else-if="[1, 2, 6].includes(room.status)" class="px-4 py-1 text-sm font-bold text-white bg-red-500 rounded-sm shadow-sm">
                  Sold
                </span>
                <span v-else-if="room.status === 3" class="px-4 py-1 text-sm font-bold text-white bg-gray-500 rounded-sm shadow-sm">
                  On Hold
                </span>
              </div>
            </div>
          </div>

          <div class="relative w-full h-full">
            <div class="relative w-full h-full">
              <unit-image :key="room.unit" :name="room.unit" :rotate="room.rotate" class="flex items-center justify-center max-h-36" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="absolute inset-y-0 right-0 flex items-center w-full max-w-2xl -mb-12 -mr-48 overflow-hidden xl:-mb-20 2xl:-mr-20 2xl:max-w-3xl">
      <floor-tower :floor="floor" class="flex-1 h-full" />
    </div>

    <div class="relative flex items-center justify-center h-6 mt-6 space-x-1 text-sm xl:h-8 xl:mt-8 xl:text-base print:hidden">
      <router-link v-for="link in getEnabledLinks()" :key="link.id" :to="{ name: 'MasterPlansFloors', params: { floorId: link.id } }" class="px-2 py-1 transition-all duration-300 xl:px-3" active-class="text-white bg-black rounded-full shadow">
        {{ link.name }}
      </router-link>
    </div>

    <app-print v-if="room" ref="print" @done="isPrinting = false">
      <div class="text-2xl font-bold uppercase">
        {{ room.UnitNumber }}
      </div>
      <div class="text-3xl font-bold uppercase">
        TYPE {{ unit.name }}
      </div>
      <div class="text-xl font-bold uppercase">
        {{ unit.type.name }}
      </div>
      <div class="text-lg">
        {{ unit.area }} sq.m.
      </div>
      <div v-if="room.sellingPrice" class="text-2xl font-compressed">
        {{ room.sellingPrice.toLocaleString('th-TH') }}
      </div>
    </app-print>
  </div>
</template>

<script>
import anime from 'animejs'
import indicator from 'ordinal/indicator'
import isElectron from 'is-electron'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { PrinterIcon } from '@zhuowenli/vue-feather-icons'
import AppPrint from '@/components/AppPrint.vue'
import UnitImage from '@/components/UnitImage.vue'
import FloorTower from '@/components/FloorTower.vue'

const GetUnitWorker = new Worker('@/workers/GetUnit.js', { type: 'module' })

export default {
  name: 'Floors',
  components: {
    AppPrint,
    PulseLoader,
    PrinterIcon,
    UnitImage,
    FloorTower,
  },
  beforeRouteUpdate (to, from, next) {
    // Get reservation data every times we change floor
    if (to.name === 'MasterPlansFloors' && !to.query.noFetch) {
      this.fetch()
    }

    next()
  },
  props: {
    floorId: {
      type: [Number, String],
      required: false,
      default: 6,
    },
  },
  data () {
    return {
      collections: {
        floors: require('@/assets/data/floors.json'),
        units: require('@/assets/data/units.json'),
        unitTypes: require('@/assets/data/unit_types.json'),
        rooms: require('@/assets/data/rooms.json'),
        // facilities: require('@/assets/data/facilities.json'),
      },

      transitionName: 'page-scale-down',

      roomName: null,
      facilityName: null,

      updatedAt: null,
      isLoading: false,

      statuses: [
        'Available',
        'Sold', // Unknown/Unavailable status
        'Sold',
        'OnHold',
        '', // Unknown/Unavailable status
        '', // Unknown/Unavailable status
        'Sold',
      ],

      isPrinting: false,
      isElectron: isElectron(),
    }
  },
  computed: {
    floor () {
      return this.collections.floors.find(floor => parseInt(this.floorId) === floor.id)
    },
    unit () {
      return this.collections.units.find(unit => unit.name === this.room.unit)
    },
    room () {
      return this.collections.rooms.find(({ name, floorName }) => name === this.roomName && floorName === this.floor.name)
    },
    rooms () {
      return this.collections.rooms.filter(room => room.floorName === this.floor.name)
    },
  },
  watch: {
    '$route' (to, from) {
      this.transitionName = (parseInt(to.params.floorId) > parseInt(from.params.floorId)) ? 'page-scale-down' : 'page-scale-up'
    },
  },
  activated () {
    this.fetch()
  },
  mounted () {
    this.collections.units.map(unit => {
      unit.type = this.collections.unitTypes.find(type => type.id === unit.typeId)
    })

    GetUnitWorker.addEventListener('message', ({ data }) => {
      this.collections.rooms = this.collections.rooms.map(room => {
        return {
          ...room,
          ...data.find(({ name, floorName }) => room.name === name && floorName === room.floorName),
        }
      })

      const indicators = this.$el.querySelectorAll('#status-indicators > *')
      const labels = document.querySelectorAll('#status-labels > *')

      indicators.forEach((element, index) => {
        const room = this.rooms.find(room => room.name === element.dataset.name)
        if (room) {
          const status = this.statuses[room.status]
          element.setAttribute('xlink:href', `#StatusIndicator${status}`)
        }
      })

      labels.forEach((element, index) => {
        const room = this.rooms.find(room => room.name === element.dataset.name)
        if (room) {
          const status = this.statuses[room.status]
          element.setAttribute('xlink:href', `#StatusLabel${status}`)
        }
      })

      this.updatedAt = new Date().toISOString()
      this.isLoading = false
    })
  },
  methods: {
    async fetch () {
      try {
        this.isLoading = true

        const { data } = await this.$axios.post('https://cmre.cpn.co.th/remapiV2/Master/GetUnit', {
          projectid: process.env.VUE_APP_API_PROJECT_ID,
        })

        GetUnitWorker.postMessage(data.data)
      } catch {
        this.isLoading = false
      }
    },
    getEnabledLinks () {
      return [].concat(this.collections.floors.filter(floor => floor.enabled))
    },
    SvgLoaded (e) {
      const roomEl = e.querySelector('.room')
      const facilityEl = e.querySelector('.facility')

      this.isAnimationFinished = false

      if (roomEl) {
        roomEl.classList.add('is-selected')

        this.facilityName = null
        this.roomName = roomEl.dataset.name
      } else if (facilityEl) {
        facilityEl.classList.add('animate-pulse')

        this.roomName = null
        this.facilityName = facilityEl.dataset.name
      } else {
        this.roomName = null
        this.facilityName = null
      }

      this.animation = anime.timeline({
        delay: anime.stagger(100, { grid: [Math.floor(Math.random() * 12) + 5, Math.floor(Math.random() * 7) + 3], from: 'center' }),
        complete: () => {
          const elements = this.$el.querySelectorAll('.room')
          elements.forEach(element => {
            element.style.opacity = null
          })
        },
      })

      this.animation.add({
        targets: '.room',
        opacity: 0.75,
      })

      this.animation.add({
        targets: '.room',
        opacity: 0,
      })

      this.animation.finished.then(() => {
        this.isAnimationFinished = true
      })
    },
    getItemInfo (e) {
      const roomEl = e.target.closest('.room')
      const facilityEl = e.target.closest('.facility')

      if (!this.isAnimationFinished) {
        return this.cancelAnimation()
      }

      const selectedRoom = this.$el.querySelector('.is-selected')
      const selectedFacility = this.$el.querySelector('.animate-pulse')

      if (roomEl || facilityEl) {
        if (selectedRoom) selectedRoom.classList.remove('is-selected')
        if (selectedFacility) selectedFacility.classList.remove('animate-pulse')
      }

      if (roomEl) {
        roomEl.classList.add('is-selected')

        this.roomName = roomEl.dataset.name
        this.facilityName = null
        this.$router.replace({
          path: this.$route.fullPath,
          query: { unitName: this.room.unit, noFetch: true },
        })
      }

      if (facilityEl) {
        facilityEl.classList.add('animate-pulse')

        this.roomName = null
        this.facilityName = facilityEl.dataset.name
      }
    },
    getFormattedFloorName () {
      const name = this.floor.name

      if (/^[\D]*$/i.test(name)) {
        return name.toUpperCase()
      }

      if (name === '12A') {
        return `${name}<sup>${indicator(name)}</sup> FLOOR`
      }

      return `${parseInt(name)}<sup>${indicator(name)}</sup> FLOOR`
    },
    cancelAnimation () {
      this.animation.seek(this.animation.duration)
    },
    print () {
      this.isPrinting = true
      this.$refs.print.print()
    },
  },
}
</script>

<style lang="postcss" scoped>
::v-deep(.room) {
  fill: theme("colors.primary.500");
  opacity: 0;
  cursor: pointer;

  &.is-selected {
    @apply opacity-70 !important;
  }
}
</style>
